import { Divider } from '@mui/material'
import { GetProductionChartDataType } from '@vae/server/src/Charts/ProductionChart/getProductionChartData'
import React, { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ObjectContext } from '../../../../../contexts/objectContext/ObjectContext'

interface ProductionSummaryProps {
	panelLinked: boolean
	hasProduction: boolean
	chartData: GetProductionChartDataType | undefined | null
}

const ProductionSummary = ({ panelLinked, hasProduction, chartData }: ProductionSummaryProps) => {
	const { electricityTaxIncluded } = useContext(ObjectContext)
	const { t } = useTranslation('pages_electricity', { keyPrefix: 'electricityTimeScroller' })

	// saved money, saved kwh, sold money, sold kwh, average benefit as snt/kwh
	const { savedKwh, savedMoney, soldKwh, soldMoney, averageBenefit } = useMemo(() => {
		const estimatedSavings = Math.max(chartData?.estimatedSavings.totalValue ?? 0, 0)
		const exchangePrices2 = chartData?.exchangePrices.groupedData ?? []

		const sold = chartData?.datasets.find(dataset => dataset.id === 'production-sold')?.data ?? []

		const savedKwh = Math.max(chartData?.totalProductionAfterSold || 0, 0)

		const soldKwh = sold.reduce((acc, val) => acc + (val || 0), 0)
		const soldMoney = sold
			.map((val, index) => {
				const price = exchangePrices2[index]?.priceWithoutAlv ?? 0
				return (val || 0) * price
			})
			.reduce((acc, val) => acc + val, 0)

		const totalProduction = savedKwh + soldKwh

		const averageBenefit =
			totalProduction > 0 ? (estimatedSavings + soldMoney) / totalProduction : 0

		return {
			savedKwh: isNaN(savedKwh) ? 0 : Math.max(savedKwh, 0),
			savedMoney: isNaN(estimatedSavings) ? 0 : Math.max(estimatedSavings / 100, 0),
			soldKwh: isNaN(soldKwh) ? 0 : soldKwh,
			soldMoney: isNaN(soldMoney) ? 0 : soldMoney / 100,
			averageBenefit: isNaN(averageBenefit) ? 0 : averageBenefit,
		}
	}, [chartData, electricityTaxIncluded])

	return (
		<div className='info'>
			{panelLinked && (
				<div className='container'>
					<span className='title'>{t('saasto')}</span>
					<span>
						{savedMoney.toFixed(2).replace('.', ',')}
						<span className='unit'> €</span>
					</span>
					<Divider
						orientation='horizontal'
						flexItem
						className='bg-background-200-light dark:bg-surface-dark'
					/>
					<span>
						{savedKwh.toFixed(2).replace('.', ',')}
						<span className='unit'> kWh</span>
					</span>
				</div>
			)}

			{panelLinked && hasProduction && (
				<Divider
					orientation='vertical'
					flexItem
					className='bg-background-200-light dark:bg-surface-dark'
				/>
			)}

			{hasProduction && (
				<div className='container'>
					<span className='title'>{t('myynti')}</span>
					<span>
						{soldMoney.toFixed(2).replace('.', ',')}
						<span className='unit'> €</span>
					</span>
					<Divider
						orientation='horizontal'
						flexItem
						className='bg-background-200-light dark:bg-surface-dark'
					/>
					<span>
						{soldKwh.toFixed(2).replace('.', ',')}
						<span className='unit'> kWh</span>
					</span>
				</div>
			)}

			{panelLinked && hasProduction && (
				<Divider
					orientation='vertical'
					flexItem
					className='bg-background-200-light dark:bg-surface-dark'
				/>
			)}

			{panelLinked && hasProduction && (
				<div className='container'>
					<span className='title'>{t('hyoty')}</span>
					<span>
						{averageBenefit.toFixed(2).replace('.', ',')}
						<span className='unit'> {t('cents')}</span>
					</span>
				</div>
			)}
		</div>
	)
}

export default ProductionSummary
